import api from './api'
import { useAuthStore } from '@/store/authStore'

const COMPLETED_KEY_PREFIX = 'onboarding:completed:'
const DRAFT_KEY_PREFIX = 'onboarding:draft:'

export type OnboardingDraft = {
  storeName?: string
  businessType?: string
  targetAudience?: string
}

function withTenantKey(prefix: string, tenantId?: string | null): string | null {
  if (!tenantId) return null
  return `${prefix}${tenantId}`
}

export function isOnboardingCompleted(tenantId?: string | null): boolean {
  if (!tenantId) return false
  const key = withTenantKey(COMPLETED_KEY_PREFIX, tenantId)
  if (key && localStorage.getItem(key) === 'true') {
    return true
  }

  // Verificación reactiva en el estado de autenticación (sincronizado desde el backend en login/me)
  const authTenant = useAuthStore.getState().tenant as any
  if (authTenant && (authTenant.id === tenantId || !tenantId) && (authTenant.onboarded || authTenant.tenantProfile?.onboardedAt)) {
    if (key) localStorage.setItem(key, 'true')
    return true
  }

  return false
}

export function setOnboardingCompleted(tenantId?: string | null, completed = true): void {
  const key = withTenantKey(COMPLETED_KEY_PREFIX, tenantId)
  if (!key) return
  localStorage.setItem(key, completed ? 'true' : 'false')

  // Sincronizar en authStore para que la sesión activa reconozca el cambio inmediatamente
  const authState = useAuthStore.getState()
  if (authState.tenant && (authState.tenant.id === tenantId || !tenantId)) {
    useAuthStore.setState({
      tenant: { ...authState.tenant, onboarded: completed },
    })
  }
}

/**
 * Consulta el backend para sincronizar si el tenant ya completó u omitió el onboarding.
 * Se utiliza en OnboardingGuard cuando se abre la app en un navegador nuevo sin localStorage previo.
 */
export async function checkAndSyncOnboardingStatus(tenantId?: string | null): Promise<boolean> {
  if (!tenantId) return false
  if (isOnboardingCompleted(tenantId)) return true

  try {
    const res = await api.get('/tenants/current')
    const onboardedAt = res.data?.tenantProfile?.onboardedAt
    if (onboardedAt) {
      setOnboardingCompleted(tenantId, true)
      return true
    }
  } catch (err) {
    console.warn('Error verificando estado de onboarding en el servidor:', err)
  }
  return false
}

/**
 * Persiste el estado de onboarding completado / omitido tanto en el backend como en el cliente.
 */
export async function persistOnboardingCompleted(tenantId?: string | null): Promise<void> {
  if (!tenantId) return
  setOnboardingCompleted(tenantId, true)

  try {
    await api.post('/tenants/onboarding/complete')
  } catch (err) {
    // Fallback defensivo si la ruta directa no responde
    try {
      await api.post(`/api/tenants/${tenantId}/profile`, {
        onboardedAt: new Date().toISOString(),
      })
    } catch (fallbackErr) {
      console.warn('Error persistiendo onboarding en backend:', fallbackErr)
    }
  }
}

export function saveOnboardingDraft(tenantId?: string | null, draft?: OnboardingDraft): void {
  const key = withTenantKey(DRAFT_KEY_PREFIX, tenantId)
  if (!key || !draft) return
  localStorage.setItem(key, JSON.stringify(draft))
}

export function loadOnboardingDraft(tenantId?: string | null): OnboardingDraft {
  const key = withTenantKey(DRAFT_KEY_PREFIX, tenantId)
  if (!key) return {}
  const raw = localStorage.getItem(key)
  if (!raw) return {}
  try {
    return JSON.parse(raw) as OnboardingDraft
  } catch {
    return {}
  }
}

